/**
 * Loads the Razorpay Checkout script (once) and opens the payment modal.
 *
 * Usage:
 *   import { openRazorpay } from "@/lib/razorpay";
 *   await openRazorpay({
 *     kind: "invoice", ref_id: invoiceId,
 *     onSuccess: (txn) => { ... },
 *   });
 */
import { api, apiError } from "./api";
import { toast } from "sonner";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC; s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * @param {Object} opts
 *  - kind: "invoice" | "tournament_team" | "donation"
 *  - ref_id: id (for invoice/tournament)
 *  - amount: rupees (for donation only)
 *  - donor: { name, email, phone, pan, address } (for donation)
 *  - onSuccess(txn)
 *  - onFailure(err)
 */
export async function openRazorpay(opts) {
  const ok = await loadScript();
  if (!ok) {
    toast.error("Could not load Razorpay checkout. Check your internet connection.");
    return;
  }

  let order;
  try {
    const { data } = await api.post("/razorpay/order", {
      kind: opts.kind,
      ref_id: opts.ref_id,
      amount: opts.amount,
      donor: opts.donor,
      member_id: opts.member_id,
    });
    order = data;
  } catch (e) {
    toast.error(apiError(e));
    return;
  }

  const options = {
    key: order.key_id,
    amount: order.amount_paise,
    currency: order.currency,
    name: "HTP Sports Hub",
    description: order.description,
    order_id: order.order_id,
    prefill: order.prefill || {},
    theme: { color: "#F97316" },
    handler: async (response) => {
      try {
        const { data } = await api.post("/razorpay/verify", {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        toast.success("Payment successful");
        if (opts.onSuccess) opts.onSuccess(data.transaction);
      } catch (e) {
        toast.error(apiError(e));
        if (opts.onFailure) opts.onFailure(e);
      }
    },
    modal: {
      ondismiss: () => {
        if (opts.onDismiss) opts.onDismiss();
      },
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (resp) => {
    toast.error(`Payment failed: ${resp?.error?.description || "unknown error"}`);
    if (opts.onFailure) opts.onFailure(resp);
  });
  rzp.open();
}
